import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import Header from "../Header";
import {ClientModal} from "../SuperAdmin/components";
import * as clientWrites from "../SuperAdmin/services";
import LiveLocationModal from "../HomePage/LiveLocationModal";
import RouteHistoryModal from "../HomePage/RouteHistoryModal";
import * as Constants from "../../utils/Constants";
import {getAuthToken, getLoggedInUserId, showToast} from "../../utils/utility";
import "../../styles/components/super-admin.css";
import "./superAdminConsole.css";
import * as api from "./consoleApi";
import {
  CLIENT_SORTS,
  DEFAULT_FILTERS,
  DEVICE_SORTS,
  DEVICE_TYPES,
  ONLINE_OPTIONS,
  ROLE_LABELS,
  TYPE_PLURALS,
  clientsParams,
  devicesParams,
  queryFromState,
  scopeOf,
  stateFromQuery,
  usersParams,
} from "./consoleModel";
import {AccessDenied, Breadcrumb, ConfirmDialog, FilterBar, LoadError, OverviewStrip, ViewSwitch} from "./ConsoleParts";
import {ClientsTable, DevicesTable, UsersTable} from "./ConsoleTables";
import {GlobalSearch, GoToClient} from "./ConsoleSearch";
import ClientHeader from "./ClientHeader";
import DevicePanel from "./DevicePanel";
import AssetSlideOver from "../../features/assets/AssetSlideOver";

const REFRESH_MS = 30000;

const EMPTY_CLIENT_FORM = {
  name: "",
  description: "",
  streetAddress: "",
  city: "",
  state: "",
  postalCode: "",
  isoCountryCode: "",
  contactName: "",
  emailId: "",
  phoneNumber: "",
  emailNotificationEnabled: false,
  whatsappNotificationEnabled: false,
  passcodeFeature: false,
};

const FEATURE_LABELS = {emailNotificationEnabled: "Email alerts", passcodeFeature: "Passcode unlock"};

const errorText = (e, fallback) => {
  const data = e?.response?.data;
  if (typeof data?.responseData === "string" && data.responseData) return data.responseData;
  if (typeof data?.message === "string" && data.message) return data.message;
  return fallback;
};

/** Bumps every 30 s while the tab is visible, so lists and counts stay current. */
function useRefreshTick() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") setTick((t) => t + 1);
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, []);
  return tick;
}

const statusOptions = (withAssigned) => [
  {value: "", label: "Any"},
  {value: "ACTIVE", label: "Active"},
  ...(withAssigned ? [{value: "ASSIGNED", label: "Assigned"}] : []),
  {value: "SUSPENDED", label: "Suspended"},
];

/**
 * The super admin console at /home: every client, every device and one device's details. Shown
 * for every super admin (Home.js); it replaced the classic SuperAdminDashboard.
 * All data comes from the super-admin-only API; the client and device actions reuse the classic
 * console's calls (../SuperAdmin/services.js) unchanged.
 */
export default function SuperAdminConsole() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(() => stateFromQuery(location.search));
  const stateRef = useRef(state);
  stateRef.current = state;
  const cameFromDevices = useRef(false);
  const tick = useRefreshTick();
  const [reload, setReload] = useState(0);
  const [denied, setDenied] = useState(false);

  // Back / forward: the address bar is the source of truth.
  useEffect(() => {
    const fromUrl = stateFromQuery(location.search);
    if (queryFromState(fromUrl) !== queryFromState(stateRef.current)) setState(fromUrl);
  }, [location.search]);

  const go = useCallback(
    (next, {push = false} = {}) => {
      setState(next);
      const qs = queryFromState(next);
      navigate({pathname: "/home", search: qs ? `?${qs}` : ""}, {replace: !push});
    },
    [navigate]
  );

  const failed = useCallback((e) => {
    if (api.isCancelled(e)) return true;
    if (api.isAccessDenied(e)) {
      setDenied(true);
      return true;
    }
    return false;
  }, []);

  // ------------------------------------------------------------------ data

  const [overview, setOverview] = useState(null);
  useEffect(() => {
    const c = new AbortController();
    api.fetchOverview(c.signal).then(setOverview).catch(failed);
    return () => c.abort();
  }, [tick, reload, failed]);

  const [options, setOptions] = useState([]);
  useEffect(() => {
    const c = new AbortController();
    api.fetchClientOptions(c.signal).then(setOptions).catch(failed);
    return () => c.abort();
  }, [reload, failed]);

  const {view, clientId, tab, deviceId} = state;
  const [clientRow, setClientRow] = useState(null);
  const [clientError, setClientError] = useState(null);
  useEffect(() => {
    if (view !== "client") {
      setClientRow(null);
      return undefined;
    }
    const c = new AbortController();
    setClientError(null);
    api
      .fetchClient(clientId, c.signal)
      .then(setClientRow)
      .catch((e) => {
        if (failed(e)) return;
        setClientError(e?.response?.status === 404 ? "This client doesn't exist." : "Couldn't load this client. Try again.");
      });
    return () => c.abort();
  }, [view, clientId, tick, reload, failed]);
  const row = clientRow && clientRow.client?.clientId === clientId ? clientRow : null;

  const scope = scopeOf(view, tab);
  const filters = state.filters[scope];
  const params = useMemo(() => {
    if (scope === "clients") return clientsParams(filters);
    if (scope === "devices") return devicesParams(filters);
    if (scope === "clientDevices") return devicesParams(filters, clientId);
    return usersParams(filters);
  }, [scope, filters, clientId]);
  const listKey = JSON.stringify([scope, clientId, params]);
  const resetKey = JSON.stringify([scope, clientId, {...params, page: null, size: null}]);

  const [list, setList] = useState({key: null, scope: null, rows: [], total: 0, loading: true, error: null});
  useEffect(() => {
    const c = new AbortController();
    setList((l) => ({...l, loading: true, error: null}));
    const request =
      scope === "clients"
        ? api.fetchClients(params, c.signal)
        : scope === "users"
        ? api.fetchUsers(clientId, params, c.signal)
        : api.fetchDevices(params, c.signal);
    request
      .then((res) => setList({key: listKey, scope, rows: res.rows, total: res.total, loading: false, error: null}))
      .catch((e) => {
        if (failed(e)) return;
        setList((l) => ({...l, loading: false, error: "Couldn't load this list. Check your connection and try again."}));
      });
    return () => c.abort();
    // listKey already captures scope, clientId and params.
  }, [listKey, tick, reload, failed]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = list.scope === scope ? list.rows : [];

  const [device, setDevice] = useState(null);
  const [deviceError, setDeviceError] = useState(null);
  useEffect(() => {
    if (deviceId == null) {
      setDevice(null);
      setDeviceError(null);
      return undefined;
    }
    const c = new AbortController();
    api
      .fetchDevice(deviceId, c.signal)
      .then((d) => {
        setDevice(d);
        setDeviceError(null);
      })
      .catch((e) => {
        if (failed(e)) return;
        setDeviceError(e?.response?.status === 404 ? "This device doesn't exist or was deleted." : "Couldn't load this device. Try again.");
      });
    return () => c.abort();
  }, [deviceId, tick, reload, failed]);
  const shownDevice = device && device.assetId === deviceId ? device : null;

  // ------------------------------------------------------------------ navigation

  const setFilters = (name, patch) => {
    const s = stateRef.current;
    const next = {...s.filters[name], ...patch};
    if (!("page" in patch)) next.page = 0;
    go({...s, filters: {...s.filters, [name]: next}});
  };
  const resetFilters = (name) => {
    const s = stateRef.current;
    go({...s, filters: {...s.filters, [name]: {...DEFAULT_FILTERS[name], size: s.filters[name].size}}});
  };
  const showView = (next) => {
    cameFromDevices.current = false;
    go({...stateRef.current, view: next, clientId: null, deviceId: null, tab: "devices"}, {push: true});
  };
  const openClient = (id, devicePatch = null) => {
    const s = stateRef.current;
    const same = s.view === "client" && s.clientId === id;
    if (s.view !== "client") cameFromDevices.current = s.view === "devices";
    go(
      {
        ...s,
        view: "client",
        clientId: id,
        deviceId: null,
        tab: devicePatch ? "devices" : same ? s.tab : "devices",
        filters: {
          ...s.filters,
          clientDevices: devicePatch || !same ? {...DEFAULT_FILTERS.clientDevices, ...(devicePatch || {})} : s.filters.clientDevices,
          users: same ? s.filters.users : {...DEFAULT_FILTERS.users},
        },
      },
      {push: true}
    );
  };
  const openDevice = (d) => go({...stateRef.current, deviceId: d.assetId}, {push: true});
  const openDeviceFromSearch = (d) => {
    const s = stateRef.current;
    if (s.view !== "client") cameFromDevices.current = s.view === "devices";
    go(
      {
        ...s,
        view: "client",
        clientId: d.clientId,
        tab: "devices",
        deviceId: d.assetId,
        filters: {...s.filters, clientDevices: {...DEFAULT_FILTERS.clientDevices}, users: {...DEFAULT_FILTERS.users}},
      },
      {push: true}
    );
  };
  const closeDevice = useCallback(() => go({...stateRef.current, deviceId: null}), [go]);
  const setTab = (next) => go({...stateRef.current, tab: next, deviceId: null});

  const jump = ({kind, value}) => {
    const s = stateRef.current;
    if (s.view === "client") {
      if (kind === "users") {
        go({...s, tab: "users", deviceId: null, filters: {...s.filters, users: {...DEFAULT_FILTERS.users}}});
        return;
      }
      const f = {...DEFAULT_FILTERS.clientDevices, size: s.filters.clientDevices.size};
      if (kind === "online" || kind === "offline") f.online = kind;
      if (kind === "type") f.type = value;
      go({...s, tab: "devices", deviceId: null, filters: {...s.filters, clientDevices: f}});
      return;
    }
    if (kind === "clients" || kind === "attention") {
      const f = {...DEFAULT_FILTERS.clients, size: s.filters.clients.size, attention: kind === "attention" ? "yes" : ""};
      cameFromDevices.current = false;
      go({...s, view: "clients", deviceId: null, filters: {...s.filters, clients: f}}, {push: true});
      return;
    }
    const f = {...DEFAULT_FILTERS.devices, size: s.filters.devices.size};
    if (kind === "online" || kind === "offline") f.online = kind;
    if (kind === "type") f.type = value;
    go({...s, view: "devices", deviceId: null, filters: {...s.filters, devices: f}}, {push: true});
  };

  // ------------------------------------------------------------------ changes (all confirmed)

  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const runConfirmed = async () => {
    setBusy(true);
    try {
      await confirm.run();
      setConfirm(null);
      setReload((r) => r + 1);
    } catch (e) {
      showToast(errorText(e, confirm.failText), {type: "error"});
    } finally {
      setBusy(false);
    }
  };

  const askToggleClientStatus = () => {
    const c = row.client;
    const active = c.status === "ACTIVE";
    setConfirm({
      title: active ? `Deactivate ${c.name}?` : `Activate ${c.name}?`,
      message: active
        ? "Its users can't sign in until you activate it again. Its devices and history are kept."
        : "Its users can sign in again.",
      confirmLabel: active ? "Deactivate" : "Activate",
      danger: active,
      failText: "Couldn't change the client's status. Try again.",
      run: async () => {
        await clientWrites.updateClientStatus(c.clientId, active ? "INACTIVE" : "ACTIVE", getLoggedInUserId());
        showToast(active ? `${c.name} deactivated` : `${c.name} activated`, {type: "success"});
      },
    });
  };

  const featureBody = (c, patch) => ({
    emailNotificationEnabled: c.emailNotificationEnabled,
    passcodeFeature: c.passcodeFeature,
    whatsappNotificationEnabled: c.whatsappNotificationEnabled,
    overspeedLimitKmh: c.overspeedLimitKmh,
    ...patch,
  });

  const askToggleFeature = (name) => {
    const c = row.client;
    const turnOn = !c[name];
    setConfirm({
      title: `Turn ${FEATURE_LABELS[name].toLowerCase()} ${turnOn ? "on" : "off"}?`,
      message: `${FEATURE_LABELS[name]} will be turned ${turnOn ? "on" : "off"} for ${c.name}.`,
      confirmLabel: turnOn ? "Turn on" : "Turn off",
      danger: !turnOn,
      failText: "Couldn't save the change. Try again.",
      run: async () => {
        await clientWrites.updateClientFeatures(c.clientId, featureBody(c, {[name]: turnOn}), getLoggedInUserId());
        showToast(`${FEATURE_LABELS[name]} ${turnOn ? "on" : "off"} for ${c.name}`, {type: "success"});
      },
    });
  };

  const askSaveOverspeed = (raw) => {
    const c = row.client;
    const text = String(raw ?? "").trim();
    const kmh = Number(text);
    if (text === "" || !Number.isInteger(kmh) || kmh < 1 || kmh > 300) {
      showToast("Enter a whole number from 1 to 300 km/h.", {type: "error"});
      return;
    }
    setConfirm({
      title: `Set the overspeed limit to ${kmh} km/h?`,
      message: `Overspeed alerts for ${c.name}'s trackers will use the new limit.`,
      confirmLabel: "Save limit",
      failText: "Couldn't save the overspeed limit. Try again.",
      run: async () => {
        await clientWrites.updateClientFeatures(c.clientId, featureBody(c, {overspeedLimitKmh: kmh}), getLoggedInUserId());
        showToast(`Overspeed limit for ${c.name} is now ${kmh} km/h`, {type: "success"});
      },
    });
  };

  const askToggleSuspend = () => {
    const d = shownDevice;
    const suspended = d.assetStatus === "SUSPENDED";
    setConfirm({
      title: suspended ? `Activate ${d.name}?` : `Suspend ${d.name}?`,
      message: suspended
        ? "The device starts reporting to its client again."
        : "If it's assigned to someone, it's returned automatically. Its history is kept.",
      confirmLabel: suspended ? "Activate" : "Suspend",
      danger: !suspended,
      failText: "Couldn't change the device's status. Try again.",
      run: async () => {
        await clientWrites.updateAssetStatus(d.clientId, d.assetId, suspended ? "ACTIVE" : "SUSPENDED", getLoggedInUserId());
        showToast(suspended ? `${d.name} activated` : `${d.name} suspended`, {type: "success"});
      },
    });
  };

  const askDeleteDevice = () => {
    const d = shownDevice;
    setConfirm({
      title: `Delete ${d.name}?`,
      message:
        "It disappears from every list and count, and if it's assigned to someone it's returned. Its IMEI or phone number can then be registered again. Its history is kept.",
      confirmLabel: "Delete device",
      danger: true,
      failText: "Couldn't delete the device. Try again.",
      run: async () => {
        await api.deleteDevice(d.clientId, d.assetId);
        showToast(`${d.name} deleted`, {type: "success"});
        closeDevice();
      },
    });
  };

  const askDeleteClient = () => {
    const c = row.client;
    const devices = row.devices ?? 0;
    const users = row.users ?? 0;
    setConfirm({
      title: `Delete ${c.name}?`,
      message: `Its ${devices} device${devices === 1 ? "" : "s"} and ${users} user${users === 1 ? "" : "s"} are deleted with it: nobody can sign in, alerts stop, and its IMEIs, phone numbers, emails and name can be registered again. Its history is kept. This can't be undone from the console.`,
      confirmLabel: "Delete client",
      danger: true,
      typeToConfirm: c.name,
      failText: "Couldn't delete the client. Try again.",
      run: async () => {
        const result = await api.deleteClient(c.clientId, c.name);
        showToast(
          `${c.name} deleted, with ${result?.devices ?? devices} device(s) and ${result?.users ?? users} user(s)`,
          {type: "success"}
        );
        showView("clients");
      },
    });
  };

  // Add or edit a client with the classic console's form.
  const [clientForm, setClientForm] = useState(null);
  const [savingClient, setSavingClient] = useState(false);
  const openEditClient = () => {
    const c = row.client;
    const form = {...EMPTY_CLIENT_FORM};
    Object.keys(EMPTY_CLIENT_FORM).forEach((k) => {
      if (c[k] !== undefined && c[k] !== null) form[k] = c[k];
    });
    setClientForm({mode: "edit", clientId: c.clientId, form});
  };
  const submitClientForm = async (e) => {
    e.preventDefault();
    if (savingClient) return;
    setSavingClient(true);
    try {
      if (clientForm.mode === "edit") {
        await clientWrites.updateClient(clientForm.clientId, clientForm.form, getLoggedInUserId());
        showToast("Client saved", {type: "success"});
      } else {
        await clientWrites.registerClient(clientForm.form, getLoggedInUserId());
        showToast("Client registered. The admin's sign-in details were emailed to them.", {type: "success"});
      }
      setClientForm(null);
      setReload((r) => r + 1);
    } catch (err) {
      showToast(errorText(err, "Couldn't save the client. Check the details and try again."), {type: "error"});
    } finally {
      setSavingClient(false);
    }
  };
  const onClientInput = (e) => {
    const {name, value, type, checked} = e.target;
    setClientForm((f) => ({...f, form: {...f.form, [name]: type === "checkbox" ? checked : value}}));
  };
  const onClientCountry = (selected) =>
    setClientForm((f) => ({...f, form: {...f.form, isoCountryCode: selected?.isoCode || ""}}));

  // Add a device to the client being shown, with the Assets page's Register asset form (the form
  // is the confirmation, as with Add client).
  const [addingDevice, setAddingDevice] = useState(false);

  // The dashboard's own live map and route history modals.
  const [modal, setModal] = useState(null);

  // ------------------------------------------------------------------ render

  if (denied) {
    return (
      <>
        <Header />
        <div className="sa-container sac-page">
          <AccessDenied />
        </div>
      </>
    );
  }

  const title = view === "client" ? row?.client?.name || "Client" : view === "devices" ? "All devices" : "All clients";
  const typeOptions = (presentOnly) => [
    {value: "", label: "All types"},
    ...DEVICE_TYPES.filter((t) => !presentOnly || row?.devicesByType?.[t]).map((t) => ({
      value: t,
      label: presentOnly ? `${TYPE_PLURALS[t]} (${row.devicesByType[t]})` : TYPE_PLURALS[t],
    })),
  ];
  const clientFilterOptions = [
    {value: "", label: "All clients"},
    ...options.map((o) => ({value: String(o.clientId), label: o.status === "ACTIVE" ? o.name : `${o.name} (inactive)`})),
  ];

  const card = (heading, count, action, body) => (
    <section className="bg-white border border-ink-200 rounded-xl overflow-hidden" aria-label={heading}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-ink-200">
        <h2 className="text-base font-semibold text-ink-900">
          {heading}
          {count != null && <span className="ml-2 text-sm font-medium text-ink-500 tabular-nums">{count.toLocaleString("en-IN")}</span>}
        </h2>
        {action}
      </div>
      {body}
    </section>
  );

  const listBody = (bar, table) => (
    <>
      {bar}
      {list.error && <LoadError message={list.error} onRetry={() => setReload((r) => r + 1)} />}
      <div className="sac-table">{table}</div>
    </>
  );

  return (
    <>
      <Header />
      <div className="sa-container sac-page flex flex-col gap-4">
        <header className="px-4 py-3 bg-white border border-ink-200 rounded-xl shadow-xs flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider font-semibold text-ink-500 leading-tight">Super Admin</p>
            <h1 className="text-base sm:text-lg font-semibold text-ink-900 leading-tight truncate">{title}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <GlobalSearch onPickClient={(id) => openClient(id)} onPickDevice={openDeviceFromSearch} />
          </div>
        </header>

        <div className="flex flex-wrap items-center justify-between gap-3">
          {view === "client" ? (
            <Breadcrumb
              backLabel={cameFromDevices.current ? "All devices" : "All clients"}
              current={row?.client?.name || "Client"}
              onBack={() => showView(cameFromDevices.current ? "devices" : "clients")}
            />
          ) : (
            <ViewSwitch view={view} clients={overview?.clients} devices={overview?.devices} onChange={showView} />
          )}
          <GoToClient options={options} clientId={view === "client" ? clientId : null} onPick={(id) => openClient(id)} onClear={() => showView("clients")} />
        </div>

        <OverviewStrip overview={view === "client" ? null : overview} row={view === "client" ? row : null} onJump={jump} />

        {view === "clients" &&
          card(
            "Clients",
            list.scope === "clients" ? list.total : null,
            <button
              type="button"
              onClick={() => setClientForm({mode: "add", form: {...EMPTY_CLIENT_FORM}})}
              className="h-9 px-3 text-sm font-semibold rounded-md bg-brand-700 text-white hover:bg-brand-800"
            >
              Add client
            </button>,
            listBody(
              <FilterBar
                idPrefix="sac-clients"
                scope="clients"
                filters={filters}
                search="Client name, city, contact"
                fields={[
                  {key: "status", label: "Status", options: [{value: "", label: "All"}, {value: "ACTIVE", label: "Active"}, {value: "INACTIVE", label: "Inactive"}]},
                  {key: "type", label: "Device type", options: [{value: "", label: "Any"}, ...DEVICE_TYPES.map((t) => ({value: t, label: `Has ${TYPE_PLURALS[t]}`}))]},
                  {key: "attention", label: "Attention", options: [{value: "", label: "All"}, {value: "yes", label: "Needs attention"}, {value: "no", label: "No issues"}]},
                  {key: "sort", label: "Sort by", options: CLIENT_SORTS},
                ]}
                onChange={(patch) => setFilters("clients", patch)}
                onReset={() => resetFilters("clients")}
              />,
              <ClientsTable
                rows={rows}
                total={list.total}
                filters={filters}
                loading={list.loading}
                resetKey={resetKey}
                onOpen={(id) => openClient(id)}
                onPage={(patch) => setFilters("clients", patch)}
              />
            )
          )}

        {view === "devices" &&
          card(
            "All devices",
            list.scope === "devices" ? list.total : null,
            null,
            listBody(
              <FilterBar
                idPrefix="sac-devices"
                scope="devices"
                filters={filters}
                search="Name, IMEI, unit ID, phone, client"
                fields={[
                  {key: "client", label: "Client", options: clientFilterOptions},
                  {key: "type", label: "Type", options: typeOptions(false)},
                  {key: "online", label: "Online", options: ONLINE_OPTIONS},
                  {key: "status", label: "Status", options: statusOptions(true)},
                  {key: "sort", label: "Sort by", options: [...DEVICE_SORTS, {value: "client", label: "Client A–Z"}]},
                ]}
                onChange={(patch) => setFilters("devices", patch)}
                onReset={() => resetFilters("devices")}
              />,
              <DevicesTable
                rows={rows}
                total={list.total}
                filters={filters}
                loading={list.loading}
                resetKey={resetKey}
                withClient
                onOpen={openDevice}
                onPage={(patch) => setFilters("devices", patch)}
              />
            )
          )}

        {view === "client" && (
          <>
            {clientError && <LoadError message={clientError} onRetry={() => setReload((r) => r + 1)} />}
            {row && (
              <ClientHeader
                row={row}
                onEdit={openEditClient}
                onToggleStatus={askToggleClientStatus}
                onToggleFeature={askToggleFeature}
                onSaveOverspeed={askSaveOverspeed}
                onDelete={askDeleteClient}
              />
            )}
            <section className="bg-white border border-ink-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between gap-2 pr-3 border-b border-ink-200">
                <div role="tablist" aria-label="Client lists" className="flex gap-1 px-2">
                  {[
                    ["devices", "Devices", row?.devices],
                    ["users", "Users", row?.users],
                  ].map(([key, label, count]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={tab === key}
                      onClick={() => setTab(key)}
                      className={`px-3 pt-3 pb-2.5 text-sm font-semibold border-b-2 ${
                        tab === key ? "border-accent-500 text-ink-900" : "border-transparent text-ink-500 hover:text-ink-800"
                      }`}
                    >
                      {label}
                      {count != null && <span className="ml-1.5 font-medium text-ink-500 tabular-nums">{count.toLocaleString("en-IN")}</span>}
                    </button>
                  ))}
                </div>
                {tab === "devices" && row?.client && (
                  <button
                    type="button"
                    onClick={() => setAddingDevice(true)}
                    className="h-8 px-3 text-sm font-semibold rounded-md bg-brand-700 text-white hover:bg-brand-800"
                  >
                    Add device
                  </button>
                )}
              </div>
              {tab === "devices"
                ? listBody(
                    <FilterBar
                      idPrefix="sac-client-devices"
                      scope="clientDevices"
                      filters={filters}
                      search="Name, IMEI, unit ID, phone"
                      fields={[
                        {key: "type", label: "Type", options: typeOptions(true)},
                        {key: "online", label: "Online", options: ONLINE_OPTIONS},
                        {key: "status", label: "Status", options: statusOptions(true)},
                        {key: "sort", label: "Sort by", options: DEVICE_SORTS},
                      ]}
                      onChange={(patch) => setFilters("clientDevices", patch)}
                      onReset={() => resetFilters("clientDevices")}
                    />,
                    <DevicesTable
                      rows={rows}
                      total={list.total}
                      filters={filters}
                      loading={list.loading}
                      resetKey={resetKey}
                      onOpen={openDevice}
                      onPage={(patch) => setFilters("clientDevices", patch)}
                    />
                  )
                : listBody(
                    <FilterBar
                      idPrefix="sac-users"
                      scope="users"
                      filters={filters}
                      search="Name, email, phone"
                      fields={[
                        {
                          key: "role",
                          label: "Role",
                          options: [
                            {value: "", label: "All roles"},
                            ...["CLIENT_ADMIN", "CLIENT_MANAGER", "CLIENT_EMPLOYEE", "MOBI_TRACK"].map((r) => ({value: r, label: ROLE_LABELS[r]})),
                          ],
                        },
                        {key: "status", label: "Status", options: statusOptions(false)},
                      ]}
                      onChange={(patch) => setFilters("users", patch)}
                      onReset={() => resetFilters("users")}
                    />,
                    <UsersTable
                      rows={rows}
                      total={list.total}
                      filters={filters}
                      loading={list.loading}
                      resetKey={resetKey}
                      onPage={(patch) => setFilters("users", patch)}
                    />
                  )}
            </section>
          </>
        )}
      </div>

      {deviceId != null && !modal && (
        <DevicePanel
          device={shownDevice}
          error={deviceError}
          onClose={closeDevice}
          onOpenClient={(id) => openClient(id)}
          onLiveMap={() => setModal("live")}
          onRouteHistory={() => setModal("route")}
          onToggleSuspend={askToggleSuspend}
          onDelete={askDeleteDevice}
        />
      )}

      {modal === "live" && shownDevice && (
        <LiveLocationModal
          assetUnitId={shownDevice.unitId}
          latitude={shownDevice.latitude}
          longitude={shownDevice.longitude}
          assetType={shownDevice.assetType}
          vehicleType={shownDevice.vehicleType}
          assetName={shownDevice.name}
          deviceStatus={shownDevice.online ? Constants.DEVICE_ONLINE : Constants.DEVICE_OFFLINE}
          initialSpeedKmph={shownDevice.online && !shownDevice.positionStale ? Number(shownDevice.telemetry?.speed) || 0 : null}
          onClose={() => setModal(null)}
        />
      )}
      {modal === "route" && shownDevice && (
        <RouteHistoryModal
          assetUnitId={shownDevice.unitId}
          assetType={shownDevice.assetType}
          vehicleType={shownDevice.vehicleType}
          assetName={shownDevice.name}
          baseUrl={Constants.BASE_URL}
          authToken={getAuthToken()}
          onClose={() => setModal(null)}
        />
      )}

      <ConfirmDialog request={confirm} busy={busy} onCancel={() => setConfirm(null)} onConfirm={runConfirmed} />
      {row?.client && (
        <AssetSlideOver
          mode="create"
          open={addingDevice}
          clientId={row.client.clientId}
          clientName={row.client.name}
          onClose={() => setAddingDevice(false)}
          onSaved={() => setReload((r) => r + 1)}
        />
      )}

      {clientForm && (
        <ClientModal
          show
          editMode={clientForm.mode === "edit"}
          formData={clientForm.form}
          onClose={() => !savingClient && setClientForm(null)}
          onInputChange={onClientInput}
          onCountryChange={onClientCountry}
          onSubmit={submitClientForm}
        />
      )}
    </>
  );
}
